import cx from 'classnames'
import { useId } from 'react'

export type IconName =
  | 'Apple'
  | 'Facebook'
  | 'Instagram'
  | 'Pinterest'
  | 'Soundcloud'
  | 'Spotify'
  | 'Twitter'
  | 'YouTube'
  | 'Github'
  | 'TikTok'
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'Plus'
  | 'Minus'
  | 'PlusCircle'
  | 'Spinner'
  | 'Checkmark'
  | 'Cross'
  | 'ChevronLeft'
  | 'ChevronRight'
  | 'ChevronUp'
  | 'ChevronDown'
  | 'Cart'
  | 'Profile'
  | 'Play'
  | 'Pause'
  | 'Quote'
  | 'PriceTag'
  | 'Copy'
  | 'Trash'
  | 'Upload'
  | 'Edit'
  | 'Store'
  | 'Logout'
  | 'Info'

interface IconProps {
  id?: string
  name: IconName
  color?: string
  viewBox?: string
  className?: string
}

const Icon = ({ id, name, color, viewBox, className }: IconProps) => {
  const defaultViewBox = getDefaultViewBox(name)
  const internalId = useId()
  const activeId = id ?? internalId

  return (
    <svg
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox ? viewBox : defaultViewBox}
      aria-labelledby={`${name.replace(/\s/g, '').toLowerCase()}-${activeId}`}
      className={cx(className, 'w-[1em] h-[1em]')}
    >
      <title id={`${name.replace(/\s/g, '').toLowerCase()}-${activeId}`}>
        {name}
      </title>
      {getIcon(name, color)}
    </svg>
  )
}

const getIcon = (name: IconName, color?: string) => {
  switch (name) {
    case 'Apple':
      return (
        <path
          d="M41.8 31.442C41.773 26.567 43.979 22.887 48.442 20.177C45.942 16.604 42.172 14.638 37.191 14.252C32.475 13.88 27.321 17.002 25.435 17.002C23.442 17.002 18.873 14.385 15.286 14.385C7.877 14.506 0 20.297 0 32.08C0.0339564 35.7567 0.680481 39.4019 1.913 42.866C3.613 47.741 9.75 59.697 16.153 59.497C19.501 59.417 21.865 57.119 26.222 57.119C30.446 57.119 32.638 59.497 36.371 59.497C42.827 59.405 48.379 48.539 50 43.65C41.339 39.572 41.8 31.695 41.8 31.442ZM34.285 9.631C35.4595 8.33729 36.3499 6.81192 36.899 5.15313C37.4481 3.49435 37.6436 1.73895 37.473 0C33.995 0.371822 30.7798 2.02435 28.453 4.636C27.2611 5.90942 26.3442 7.41457 25.7592 9.05774C25.1743 10.7009 24.9339 12.4469 25.053 14.187C28.52 14.45 31.682 12.673 34.285 9.631Z"
          fill={color}
          className="fill-current"
        />
      )

    case 'Facebook':
      return (
        <path
          d="M8.989 60.0004V33.1644H0V22.5004H8.989V14.1004C8.989 4.96943 14.569 0.000426164 22.711 0.000426164C25.459 -0.00880927 28.2054 0.132068 30.938 0.422427V9.96143H25.289C20.859 9.96143 20.004 12.0704 20.004 15.1524V22.5004H30.004L28.629 33.1644H20.004V60.0004"
          fill={color}
          className="fill-current"
        />
      )

    case 'Instagram':
      return (
        <path
          d="M30.0118 14.6137C26.9699 14.6137 23.9963 15.5158 21.4671 17.2057C18.9379 18.8957 16.9666 21.2978 15.8025 24.1081C14.6384 26.9184 14.3338 30.0108 14.9273 32.9942C15.5207 35.9777 16.9855 38.7181 19.1364 40.8691C21.2874 43.02 24.0278 44.4848 27.0113 45.0782C29.9947 45.6717 33.0871 45.3671 35.8974 44.203C38.7077 43.0389 41.1098 41.0676 42.7998 38.5384C44.4897 36.0092 45.3918 33.0356 45.3918 29.9938C45.395 27.9731 44.9995 25.9717 44.2277 24.1042C43.456 22.2367 42.3232 20.5399 40.8944 19.1111C39.4656 17.6823 37.7688 16.5495 35.9013 15.7778C34.0338 15.006 32.0324 14.6105 30.0118 14.6137V14.6137ZM30.0118 39.9928C28.0339 39.9928 26.1005 39.4063 24.456 38.3074C22.8116 37.2086 21.5298 35.6468 20.773 33.8196C20.0161 31.9923 19.818 29.9817 20.2039 28.0418C20.5897 26.102 21.5422 24.3202 22.9407 22.9217C24.3392 21.5232 26.121 20.5708 28.0608 20.1849C30.0007 19.799 32.0113 19.9971 33.8386 20.754C35.6658 21.5108 37.2276 22.7926 38.3264 24.437C39.4253 26.0815 40.0117 28.0149 40.0117 29.9928C40.0073 32.6435 38.9522 35.1845 37.0779 37.0589C35.2035 38.9332 32.6625 39.9883 30.0118 39.9928V39.9928ZM49.6047 13.9848C49.604 14.6942 49.3928 15.3874 48.9981 15.9768C48.6033 16.5662 48.0427 17.0253 47.387 17.2961C46.7313 17.5669 46.01 17.6372 45.3144 17.4981C44.6188 17.359 43.98 17.0168 43.4789 16.5147C42.9778 16.0126 42.6368 15.3731 42.499 14.6772C42.3613 13.9813 42.433 13.2602 42.705 12.6051C42.9771 11.9499 43.4373 11.3901 44.0275 10.9965C44.6177 10.6029 45.3113 10.3932 46.0207 10.3938C46.4922 10.3931 46.9591 10.4856 47.3947 10.6659C47.8303 10.8462 48.2259 11.1108 48.559 11.4445C48.892 11.7782 49.1558 12.1743 49.3353 12.6103C49.5148 13.0462 49.6063 13.5133 49.6047 13.9848V13.9848ZM59.7908 17.6258C59.5628 12.8208 58.4658 8.56375 54.9448 5.05675C51.4238 1.54975 47.1807 0.45675 42.3757 0.21075C37.4227 -0.07025 22.5757 -0.07025 17.6257 0.21075C12.8337 0.43875 8.57675 1.53575 5.05675 5.04275C1.53675 8.54975 0.45675 12.8068 0.21075 17.6118C-0.07025 22.5648 -0.07025 37.4118 0.21075 42.3618C0.43875 47.1668 1.53575 51.4237 5.05675 54.9307C8.57775 58.4377 12.8207 59.5307 17.6257 59.7767C22.5787 60.0577 37.4257 60.0577 42.3757 59.7767C47.1807 59.5487 51.4378 58.4517 54.9448 54.9307C58.4518 51.4097 59.5448 47.1668 59.7908 42.3618C60.0718 37.4088 60.0718 22.5778 59.7908 17.6258V17.6258ZM53.3908 47.6758C52.8817 48.9656 52.1131 50.1371 51.1326 51.1176C50.1521 52.0981 48.9806 52.8667 47.6907 53.3758C43.7417 54.9418 34.3728 54.5758 30.0088 54.5758C25.6448 54.5758 16.2617 54.9238 12.3267 53.3758C11.0369 52.8667 9.86542 52.0981 8.88492 51.1176C7.90441 50.1371 7.1358 48.9656 6.62675 47.6758C5.06075 43.7268 5.42675 34.3578 5.42675 29.9938C5.42675 25.6298 5.07875 16.2468 6.62675 12.3118C7.1358 11.0219 7.90441 9.85042 8.88492 8.86991C9.86542 7.88941 11.0369 7.1208 12.3267 6.61175C16.2757 5.04575 25.6448 5.41175 30.0088 5.41175C34.3728 5.41175 43.7557 5.06375 47.6907 6.61175C48.9806 7.1208 50.1521 7.88941 51.1326 8.86991C52.1131 9.85042 52.8817 11.0219 53.3908 12.3118C54.9618 16.2608 54.6047 25.6298 54.6047 29.9938C54.6047 34.3578 54.9617 43.7408 53.3957 47.6758H53.3908Z"
          fill={color}
          className="fill-current"
        />
      )

    case 'Pinterest':
      return (
        <path
          d="M31.5 0C14.105 0 0 14.105 0 31.5C0 44.852 8.297 56.258 20.016 60.848C19.731 58.36 19.496 54.524 20.118 51.801L23.802 36.141C23.802 36.141 22.868 34.25 22.868 31.473C22.868 27.094 25.407 23.828 28.575 23.828C31.266 23.828 32.563 25.848 32.563 28.258C32.563 30.957 30.856 35 29.946 38.762C29.192 41.899 31.528 44.461 34.614 44.461C40.216 44.461 44.516 38.555 44.516 30.047C44.516 22.504 39.098 17.242 31.348 17.242C22.375 17.242 17.114 23.957 17.114 30.906C17.114 33.601 18.149 36.504 19.446 38.086C19.708 38.395 19.731 38.68 19.653 38.992L18.77 42.57C18.645 43.14 18.305 43.269 17.708 42.984C13.771 41.144 11.306 35.414 11.306 30.773C11.306 20.843 18.513 11.718 32.126 11.718C43.04 11.718 51.544 19.495 51.544 29.917C51.544 40.78 44.696 49.519 35.212 49.519C32.021 49.519 29.013 47.859 28.005 45.89L26.032 53.382C25.333 56.128 23.415 59.55 22.118 61.652C25.1414 62.5815 28.287 63.0528 31.45 63.05C48.848 63.05 62.95 48.948 62.95 31.55C63 14.105 48.895 0 31.5 0Z"
          fill={color}
          className="fill-current"
        />
      )

    case 'Soundcloud':
      return (
        <path
          d="M11.316 14.1401L11.905 20.7401L11.316 27.6781C11.3083 27.7941 11.2587 27.9034 11.1765 27.9856C11.0943 28.0678 10.985 28.1174 10.869 28.1251C10.7534 28.1217 10.6437 28.0729 10.5638 27.9892C10.4839 27.9056 10.4401 27.7938 10.442 27.6781L9.873 20.7401L10.442 14.1401C10.4443 14.0276 10.49 13.9203 10.5696 13.8407C10.6492 13.7611 10.7565 13.7154 10.869 13.7131C10.9814 13.7215 11.0874 13.7687 11.1689 13.8466C11.2505 13.9245 11.3024 14.0282 11.316 14.1401V14.1401ZM13.49 9.50811C13.3565 9.51189 13.2294 9.56662 13.135 9.66108C13.0405 9.75555 12.9858 9.88257 12.982 10.0161L12.474 20.7431L12.982 27.6811C12.9858 27.8146 13.0405 27.9417 13.135 28.0361C13.2294 28.1306 13.3565 28.1853 13.49 28.1891C13.62 28.1831 13.7425 28.1267 13.8316 28.0319C13.9206 27.937 13.9692 27.8112 13.967 27.6811L14.556 20.7431L13.967 10.0151C13.969 9.88519 13.9203 9.7596 13.8312 9.66497C13.7422 9.57033 13.6198 9.51407 13.49 9.50811V9.50811ZM16.08 7.06011C15.9338 7.05815 15.7926 7.11325 15.6864 7.2137C15.5801 7.31414 15.5172 7.45204 15.511 7.59811L15.064 20.8041L15.511 27.6911C15.5172 27.8372 15.5801 27.9751 15.6864 28.0755C15.7926 28.176 15.9338 28.2311 16.08 28.2291C16.1508 28.2295 16.2209 28.2159 16.2864 28.189C16.3518 28.1621 16.4113 28.1225 16.4613 28.0724C16.5113 28.0224 16.551 27.9629 16.5779 27.8975C16.6048 27.832 16.6184 27.7619 16.618 27.6911L17.156 20.8041L16.618 7.59911C16.6184 7.52835 16.6048 7.45821 16.5779 7.39275C16.551 7.3273 16.5113 7.26783 16.4613 7.2178C16.4113 7.16776 16.3518 7.12815 16.2864 7.10125C16.2209 7.07435 16.1508 7.06071 16.08 7.06111V7.06011ZM0.731001 16.8731C0.6651 16.8776 0.603046 16.9057 0.556341 16.9524C0.509636 16.9992 0.481446 17.0612 0.476999 17.1271L0 20.7431L0.476999 24.2981C0.476999 24.3655 0.503761 24.4301 0.551395 24.4777C0.59903 24.5253 0.663636 24.5521 0.731001 24.5521C0.798366 24.5521 0.86297 24.5253 0.910604 24.4777C0.958239 24.4301 0.985001 24.3655 0.985001 24.2981L1.554 20.7431L0.985001 17.1271C0.980554 17.0612 0.952364 16.9992 0.905659 16.9524C0.858954 16.9057 0.796902 16.8776 0.731001 16.8731V16.8731V16.8731ZM3.131 14.6481C3.09753 14.6477 3.06432 14.654 3.03333 14.6666C3.00233 14.6792 2.97417 14.6979 2.9505 14.7216C2.92683 14.7453 2.90814 14.7734 2.89552 14.8044C2.88289 14.8354 2.8766 14.8686 2.877 14.9021L2.227 20.7431L2.877 26.4431C2.87369 26.4787 2.87777 26.5146 2.88897 26.5486C2.90016 26.5825 2.91825 26.6138 2.94209 26.6405C2.96593 26.6671 2.99501 26.6886 3.02751 26.7035C3.06002 26.7184 3.09525 26.7264 3.131 26.7271C3.20043 26.7242 3.2667 26.6973 3.3185 26.651C3.3703 26.6047 3.40441 26.5418 3.415 26.4731L4.146 20.7441L3.415 14.9031C3.40455 14.8339 3.37024 14.7706 3.31801 14.724C3.26579 14.6774 3.19893 14.6506 3.129 14.6481H3.131ZM5.701 13.4901C5.61349 13.4942 5.53066 13.5309 5.46871 13.5928C5.40675 13.6548 5.37013 13.7376 5.366 13.8251L4.777 20.7421L5.366 27.4261C5.37742 27.509 5.4169 27.5855 5.47786 27.6428C5.53881 27.7001 5.61757 27.7348 5.701 27.7411C5.78446 27.7408 5.86443 27.7076 5.92345 27.6486C5.98246 27.5895 6.01574 27.5096 6.016 27.4261L6.716 20.7421L6.016 13.8251C6.01788 13.7823 6.01116 13.7395 5.99624 13.6994C5.98133 13.6592 5.95852 13.6224 5.92916 13.5912C5.8998 13.5599 5.86449 13.5349 5.8253 13.5176C5.78612 13.5002 5.74385 13.4909 5.701 13.4901V13.4901ZM8.271 13.2671C8.22264 13.266 8.17455 13.2747 8.12965 13.2927C8.08475 13.3107 8.04397 13.3377 8.00976 13.3719C7.97556 13.4061 7.94864 13.4469 7.93063 13.4918C7.91263 13.5367 7.90391 13.5847 7.905 13.6331L7.316 20.7431L7.9 27.6301C7.89891 27.6785 7.90763 27.7266 7.92563 27.7715C7.94364 27.8164 7.97056 27.8571 8.00476 27.8913C8.03897 27.9256 8.07976 27.9525 8.12465 27.9705C8.16955 27.9885 8.21764 27.9972 8.266 27.9961C8.36645 27.9965 8.46336 27.959 8.53747 27.8912C8.61158 27.8234 8.65746 27.7302 8.666 27.6301L9.316 20.7431L8.666 13.6331C8.65746 13.533 8.61158 13.4398 8.53747 13.372C8.46336 13.3042 8.36645 13.2667 8.266 13.2671H8.271ZM32.789 2.00211C32.6633 1.91459 32.515 1.86527 32.362 1.86011C32.1563 1.86001 31.9562 1.92786 31.793 2.05311C31.5996 2.22987 31.48 2.47304 31.458 2.73411V2.81511L31.124 20.7631L31.297 24.0631L31.47 27.2831C31.4834 27.515 31.5833 27.7334 31.7499 27.8952C31.9166 28.057 32.1378 28.1505 32.37 28.1571C32.6013 28.1555 32.8227 28.063 32.9863 27.8994C33.1498 27.7358 33.2424 27.5144 33.244 27.2831L33.644 20.7621L33.244 2.73211C33.2287 2.58345 33.1796 2.44026 33.1004 2.31354C33.0212 2.18682 32.914 2.07994 32.787 2.00111V2.00111L32.789 2.00211ZM30.077 3.55611C29.9432 3.47015 29.7889 3.42147 29.63 3.41511C29.4707 3.42022 29.316 3.46937 29.183 3.55711C29.0694 3.63045 28.9763 3.73137 28.9123 3.85046C28.8483 3.96955 28.8155 4.10292 28.817 4.23811L28.787 4.41111L28.5 20.7431C28.5 20.7431 28.5 20.7731 28.815 27.4071V27.4371C28.8168 27.6111 28.8778 27.7794 28.988 27.9141C29.0675 28.0101 29.1667 28.0878 29.2788 28.1422C29.3909 28.1965 29.5134 28.2262 29.638 28.2291C29.8536 28.2222 30.0579 28.131 30.207 27.9751C30.2904 27.9063 30.3567 27.8191 30.4008 27.7204C30.4449 27.6217 30.4655 27.5141 30.461 27.4061L30.491 26.7251L30.806 20.7731L30.474 4.23711C30.4627 4.10086 30.4209 3.9689 30.3516 3.85101C30.2824 3.73312 30.1875 3.63233 30.074 3.55611V3.55611H30.077ZM18.762 5.84111C18.6027 5.84834 18.4525 5.91694 18.3427 6.03252C18.2329 6.1481 18.1721 6.30169 18.173 6.46111L17.725 20.7431L18.172 27.5691C18.172 27.7294 18.2357 27.8832 18.3491 27.9966C18.4624 28.1099 18.6162 28.1736 18.7765 28.1736C18.9368 28.1736 19.0906 28.1099 19.2039 27.9966C19.3173 27.8832 19.381 27.7294 19.381 27.5691L19.889 20.7431L19.381 6.46111C19.3769 6.29795 19.3103 6.1426 19.1949 6.02719C19.0795 5.91179 18.9242 5.84517 18.761 5.84111V5.84111H18.762ZM57.027 12.2201C55.9604 12.2216 54.9045 12.4323 53.919 12.8401C53.6005 9.33347 51.983 6.07244 49.384 3.69692C46.785 1.32139 43.3921 0.00288178 39.871 0.000107187C38.123 -0.00676918 36.3895 0.317269 34.762 0.955109C34.5353 0.979851 34.3268 1.09063 34.1795 1.26461C34.0321 1.43858 33.9571 1.66248 33.97 1.89011V27.2541C33.9745 27.4904 34.0663 27.7166 34.2277 27.8892C34.3891 28.0618 34.6086 28.1687 34.844 28.1891H57.016C59.1335 28.1904 61.1648 27.3505 62.663 25.8542C64.1612 24.3578 65.0037 22.3276 65.005 20.2101C65.0063 18.0926 64.1664 16.0613 62.6701 14.5631C61.1737 13.0649 59.1435 12.2224 57.026 12.2211V12.2211L57.027 12.2201ZM26.888 6.09511C26.6791 6.09871 26.4797 6.18331 26.332 6.33106C26.1842 6.47882 26.0996 6.67818 26.096 6.88711L25.76 20.7731L26.1 27.4371C26.1097 27.6412 26.1972 27.8339 26.3444 27.9756C26.4917 28.1173 26.6876 28.1973 26.892 28.1991C26.9924 28.2002 27.0919 28.1812 27.1849 28.1433C27.2778 28.1054 27.3622 28.0493 27.4332 27.9783C27.5042 27.9073 27.5603 27.8229 27.5982 27.73C27.6361 27.637 27.6551 27.5375 27.654 27.4371L28.054 20.7731L27.654 6.88711C27.657 6.68133 27.5786 6.4827 27.4359 6.33439C27.2932 6.18609 27.0977 6.10009 26.892 6.09511V6.09511H26.888ZM21.443 5.30311C21.2685 5.31299 21.1042 5.38884 20.9836 5.51529C20.8629 5.64173 20.7947 5.80932 20.793 5.98411L20.4 20.7431L20.8 27.5431C20.8067 27.7151 20.8797 27.8779 21.0038 27.9973C21.1279 28.1166 21.2933 28.1833 21.4655 28.1833C21.6377 28.1833 21.8031 28.1166 21.9272 27.9973C22.0513 27.8779 22.1243 27.7151 22.131 27.5431L22.578 20.7431L22.124 5.98411C22.1155 5.80624 22.041 5.63792 21.9151 5.51201C21.7892 5.38609 21.6209 5.31161 21.443 5.30311V5.30311ZM24.155 5.64811C24.0631 5.64811 23.9721 5.66622 23.8871 5.70139C23.8022 5.73657 23.725 5.78813 23.66 5.85313C23.595 5.91814 23.5435 5.9953 23.5083 6.08023C23.4731 6.16516 23.455 6.25619 23.455 6.34811L23.055 20.7421L23.455 27.4871C23.4591 27.6715 23.5341 27.8472 23.6645 27.9776C23.7949 28.108 23.9706 28.183 24.155 28.1871C24.2469 28.1871 24.338 28.169 24.4229 28.1338C24.5078 28.0986 24.585 28.0471 24.65 27.9821C24.715 27.9171 24.7665 27.8399 24.8017 27.755C24.8369 27.6701 24.855 27.579 24.855 27.4871L25.282 20.7421L24.856 6.34911C24.8573 6.25681 24.8402 6.16518 24.8055 6.07964C24.7708 5.99411 24.7192 5.9164 24.654 5.85113C24.5887 5.78586 24.511 5.73435 24.4255 5.69965C24.3399 5.66495 24.2483 5.64776 24.156 5.64911V5.64911L24.155 5.64811Z"
          fill={color}
          className="fill-current"
        />
      )

    case 'Spotify':
      return (
        <>
          <path
            d="M42.376 36.6827C41.7768 36.668 41.196 36.4725 40.71 36.1217C30.993 30.2667 19.688 30.0217 8.52302 32.3067C7.91933 32.4981 7.29891 32.632 6.67002 32.7067C6.34563 32.7116 6.02356 32.6512 5.72291 32.5293C5.42225 32.4074 5.14913 32.2264 4.91972 31.997C4.69032 31.7676 4.5093 31.4945 4.38739 31.1938C4.26548 30.8932 4.20517 30.5711 4.21002 30.2467C4.16646 29.6236 4.36082 29.0071 4.75388 28.5216C5.14694 28.0361 5.70947 27.7178 6.32802 27.6307C19.08 24.8177 32.114 25.0657 43.232 31.7177C43.7138 31.9433 44.1159 32.3094 44.3854 32.768C44.655 33.2266 44.7793 33.7561 44.742 34.2867C44.7483 34.6008 44.6916 34.913 44.5753 35.2049C44.459 35.4967 44.2854 35.7623 44.0648 35.986C43.8442 36.2097 43.581 36.3869 43.2908 36.5073C43.0006 36.6276 42.6892 36.6886 42.375 36.6867V36.6867L42.376 36.6827Z"
            fill={color}
            className="fill-current"
          />
          <path
            d="M46.564 26.4667C45.8787 26.4236 45.2175 26.1978 44.649 25.8127C34.917 20.0507 20.404 17.7307 7.49402 21.2347C6.89958 21.4591 6.27509 21.5939 5.64102 21.6347C4.84074 21.6339 4.0734 21.3159 3.50714 20.7504C2.94088 20.1849 2.62187 19.418 2.62002 18.6177C2.5652 17.8795 2.77947 17.1464 3.22322 16.554C3.66697 15.9615 4.31021 15.5497 5.03402 15.3947C9.97858 13.9437 15.1102 13.2304 20.263 13.2777C30.369 13.2777 40.133 15.7847 47.825 20.3627C48.3836 20.6491 48.8472 21.0914 49.1596 21.6359C49.472 22.1805 49.6197 22.8039 49.585 23.4307C49.5875 23.8288 49.5112 24.2235 49.3605 24.592C49.2097 24.9605 48.9875 25.2955 48.7067 25.5777C48.4259 25.86 48.092 26.0838 47.7242 26.2364C47.3565 26.389 46.9622 26.4672 46.564 26.4667V26.4667Z"
            fill={color}
            className="fill-current"
          />
          <path
            d="M51.392 14.6017C50.6768 14.6045 49.977 14.3931 49.383 13.9947C38.296 7.3767 18.473 5.7887 5.64102 9.3697C4.99313 9.59552 4.31699 9.73014 3.63202 9.7697C3.1503 9.77438 2.67255 9.68216 2.22718 9.49854C1.78181 9.31491 1.3779 9.04363 1.03945 8.7008C0.701011 8.35797 0.434945 7.9506 0.257068 7.5029C0.0791912 7.0552 -0.00686391 6.57631 0.0040161 6.0947C-0.0350748 5.25855 0.212137 4.43408 0.704837 3.75738C1.19754 3.08069 1.90627 2.59225 2.71402 2.3727C8.67113 0.720982 14.8327 -0.0759801 21.014 0.00569682C32.381 0.00569682 44.294 2.3727 52.999 7.4487C53.6436 7.7718 54.1782 8.27827 54.5356 8.90441C54.8931 9.53055 55.0575 10.2484 55.008 10.9677C55.0107 11.4436 54.9191 11.9153 54.7387 12.3557C54.5582 12.7961 54.2924 13.1964 53.9566 13.5336C53.6208 13.8709 53.2215 14.1383 52.7819 14.3206C52.3423 14.5029 51.8709 14.5964 51.395 14.5957V14.5957L51.392 14.6017Z"
            fill={color}
            className="fill-current"
          />
        </>
      )

    case 'Twitter':
      return (
        <path
          d="M44.861 10.1216C44.893 10.5656 44.893 11.0096 44.893 11.4546C44.892 25.0056 34.581 40.6056 15.736 40.6056C10.1571 40.6201 4.69271 39.0228 0 36.0056C0.821818 36.0962 1.64823 36.1386 2.475 36.1326C7.08768 36.1461 11.5704 34.6052 15.2 31.7586C13.0617 31.7196 10.989 31.0137 9.27129 29.7396C7.55361 28.4654 6.27674 26.6866 5.619 24.6516C6.2591 24.7531 6.90593 24.8062 7.554 24.8106C8.46509 24.8083 9.37225 24.691 10.254 24.4616C7.93244 23.9937 5.84457 22.7363 4.3453 20.903C2.84604 19.0698 2.02791 16.7739 2.03 14.4056V14.2776C3.45002 15.0698 5.03715 15.5153 6.662 15.5776C4.48724 14.1269 2.94806 11.901 2.35859 9.35408C1.76911 6.80718 2.17378 4.13134 3.49 1.87264C6.06384 5.04086 9.27484 7.63273 12.9148 9.48018C16.5547 11.3276 20.5423 12.3894 24.619 12.5966C24.4545 11.8247 24.3693 11.0379 24.365 10.2486C24.3667 8.19314 24.9859 6.18555 26.1424 4.48623C27.2988 2.78691 28.9392 1.47426 30.8507 0.718486C32.7622 -0.0372834 34.8567 -0.201297 36.8626 0.247714C38.8684 0.696726 40.6931 1.73805 42.1 3.23664C44.3936 2.79415 46.5929 1.95671 48.6 0.761638C47.8364 3.1277 46.2366 5.13429 44.1 6.40564C46.1345 6.17528 48.1232 5.64205 50 4.82364C48.5973 6.86682 46.8599 8.65864 44.861 10.1236V10.1236V10.1216Z"
          fill={color}
          className="fill-current"
        />
      )

    case 'YouTube':
      return (
        <path
          d="M0 25.0369C0 26.4155 0.447422 27.5873 1.34227 28.5524C2.23711 29.5175 3.32647 30 4.61035 30H33.3897C34.6735 30 35.7629 29.5175 36.6577 28.5524C37.5526 27.5873 38 26.4155 38 25.0369V5.04591C38 3.66733 37.5482 2.48171 36.6445 1.48902C35.7408 0.496341 34.6562 0 33.3907 0H4.61137C3.34582 0 2.26121 0.496341 1.35754 1.48902C0.453872 2.48171 0.00203682 3.66733 0.00203682 5.04591V25.0369H0ZM14.3759 21.4252V8.65871C14.3759 8.53031 14.4122 8.42917 14.4849 8.35531C14.5392 8.33667 14.5843 8.32735 14.6203 8.32735L14.7558 8.35531L26.5795 14.7525C26.6522 14.8264 26.6885 14.9182 26.6885 15.028C26.6885 15.1018 26.6522 15.1753 26.5795 15.2485L12.7189 21.6457C12.683 21.6823 12.6466 21.7006 12.61 21.7006C12.5373 21.7006 12.483 21.6823 12.447 21.6457C12.3744 21.5719 14.3749 21.4983 14.3749 21.4252H14.3759Z"
          fill={color}
          className="fill-current"
        />
      )

    case 'Github':
      return (
        <path
          d="M30.6973 0C23.4069 0.00183594 16.355 2.59766 10.8033 7.32304C5.2516 12.0484 1.5624 18.595 0.395756 25.7915C-0.770884 32.988 0.66116 40.3648 4.43567 46.602C8.21018 52.8393 14.0809 57.53 20.9973 59.835C22.5323 60.117 23.0973 59.169 23.0973 58.355C23.0973 57.627 23.0703 55.696 23.0553 53.134C14.5153 54.989 12.7133 49.018 12.7133 49.018C11.3133 45.471 9.30433 44.527 9.30433 44.527C6.51733 42.622 9.51534 42.661 9.51534 42.661C12.5963 42.878 14.2153 45.825 14.2153 45.825C16.9543 50.516 21.4013 49.162 23.1503 48.376C23.29 46.8195 23.9804 45.3639 25.0973 44.271C18.2803 43.496 11.1123 40.862 11.1123 29.097C11.0679 26.0468 12.1999 23.0966 14.2733 20.859C13.3357 18.2108 13.443 15.3048 14.5733 12.733C14.5733 12.733 17.1503 11.908 23.0153 15.881C28.0469 14.5023 33.3567 14.5023 38.3883 15.881C44.2503 11.908 46.8233 12.733 46.8233 12.733C47.9549 15.3045 48.0622 18.211 47.1233 20.859C49.1969 23.0961 50.3274 26.0471 50.2793 29.097C50.2793 40.897 43.1013 43.487 36.2613 44.247C37.3613 45.195 38.3453 47.069 38.3453 49.933C38.3453 54.033 38.3083 57.349 38.3083 58.355C38.3083 59.176 38.8613 60.132 40.4193 59.832C47.3329 57.5226 53.1995 52.8291 56.97 46.5909C60.7404 40.3528 62.1688 32.9768 60.9998 25.7821C59.8307 18.5873 56.1404 12.0431 50.5888 7.31965C45.0372 2.59622 37.9864 0.00167061 30.6973 0V0Z"
          fill={color}
          className="fill-current"
        />
      )

    case 'TikTok': {
      return (
        <path
          d="M16.656 1.029c1.637-0.025 3.262-0.012 4.886-0.025 0.054 2.031 0.878 3.859 2.189 5.213l-0.002-0.002c1.411 1.271 3.247 2.095 5.271 2.235l0.028 0.002v5.036c-1.912-0.048-3.71-0.489-5.331-1.247l0.082 0.034c-0.784-0.377-1.447-0.764-2.077-1.196l0.052 0.034c-0.012 3.649 0.012 7.298-0.025 10.934-0.103 1.853-0.719 3.543-1.707 4.954l0.020-0.031c-1.652 2.366-4.328 3.919-7.371 4.011l-0.014 0c-0.123 0.006-0.268 0.009-0.414 0.009-1.73 0-3.347-0.482-4.725-1.319l0.040 0.023c-2.508-1.509-4.238-4.091-4.558-7.094l-0.004-0.041c-0.025-0.625-0.037-1.25-0.012-1.862 0.49-4.779 4.494-8.476 9.361-8.476 0.547 0 1.083 0.047 1.604 0.136l-0.056-0.008c0.025 1.849-0.050 3.699-0.050 5.548-0.423-0.153-0.911-0.242-1.42-0.242-1.868 0-3.457 1.194-4.045 2.861l-0.009 0.030c-0.133 0.427-0.21 0.918-0.21 1.426 0 0.206 0.013 0.41 0.037 0.61l-0.002-0.024c0.332 2.046 2.086 3.59 4.201 3.59 0.061 0 0.121-0.001 0.181-0.004l-0.009 0c1.463-0.044 2.733-0.831 3.451-1.994l0.010-0.018c0.267-0.372 0.45-0.822 0.511-1.311l0.001-0.014c0.125-2.237 0.075-4.461 0.087-6.698 0.012-5.036-0.012-10.060 0.025-15.083z"
          fill={color}
          className="fill-current"
        />
      )
    }

    case 'ArrowLeft':
      return (
        <>
          <path
            d="M32.8704 10.9995L0.975439 10.9995"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
          <path
            d="M1.19634 11.0005C3.94743 11.0005 6.58584 12.0768 8.53116 13.9926C10.4765 15.9085 11.5693 18.5069 11.5693 21.2163"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
          <path
            d="M1.19634 10.9995C3.94743 10.9995 6.58584 9.9232 8.53116 8.00736C10.4765 6.09152 11.5693 3.49309 11.5693 0.783679"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
        </>
      )

    case 'ArrowRight':
      return (
        <>
          <path
            d="M0.129639 11.0005L32.0246 11.0005"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
          <path
            d="M31.8037 10.9995C29.0526 10.9995 26.4142 9.92322 24.4688 8.00738C22.5235 6.09154 21.4307 3.4931 21.4307 0.783691"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
          <path
            d="M31.8037 11.0005C29.0526 11.0005 26.4142 12.0768 24.4688 13.9926C22.5235 15.9085 21.4307 18.5069 21.4307 21.2163"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
        </>
      )

    case 'Plus':
      return (
        <>
          <line
            x1="8"
            y1="13.25"
            x2="21"
            y2="13.25"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
          />
          <line
            x1="14.75"
            y1="7"
            x2="14.75"
            y2="20"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
          />
        </>
      )

    case 'Minus':
      return (
        <line
          x1="8"
          y1="13.25"
          x2="20"
          y2="13.25"
          stroke={color}
          className="stroke-current"
          strokeWidth="1.5"
        />
      )

    case 'PlusCircle':
      return (
        <>
          <path
            d="M22.2315 43.6504C34.0911 43.6504 43.7052 34.1364 43.7052 22.4004C43.7052 10.6643 34.0911 1.15039 22.2315 1.15039C10.3719 1.15039 0.757812 10.6643 0.757812 22.4004C0.757812 34.1364 10.3719 43.6504 22.2315 43.6504Z"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
          />
          <path
            d="M22.2314 8.40039V36.4004"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
          />
          <path
            d="M36.3787 22.4004H8.08398"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
          />
        </>
      )

    case 'Checkmark':
      return (
        <path
          d="M7 15L11 19L20 10"
          stroke={color}
          className="stroke-current"
          strokeWidth="1.5"
          strokeLinecap="square"
        />
      )

    case 'Cross':
      return (
        <>
          ]
          <path
            d="M6 22L22 6"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeLinecap="square"
          />
          <path
            d="M6 6L22 22"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeLinecap="square"
          />
        </>
      )

    case 'ChevronLeft':
      return (
        <>
          <path
            d="M10 14.0439C12.1101 14.0439 14.1337 14.8822 15.6258 16.3742C17.1178 17.8663 17.9561 19.8899 17.9561 22"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
          <path
            d="M10 14.0439C12.1101 14.0439 14.1337 13.2057 15.6258 11.7137C17.1178 10.2216 17.9561 8.19797 17.9561 6.08789"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
        </>
      )

    case 'ChevronRight':
      return (
        <>
          <path
            d="M18 13.9561C15.8899 13.9561 13.8663 13.1178 12.3742 11.6258C10.8822 10.1337 10.0439 8.11008 10.0439 6"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
          <path
            d="M18 13.9561C15.8899 13.9561 13.8663 14.7943 12.3742 16.2863C10.8822 17.7784 10.0439 19.802 10.0439 21.9121"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
        </>
      )

    case 'ChevronDown':
      return (
        <>
          <path
            d="M14.0439 18C14.0439 15.8899 14.8822 13.8663 16.3742 12.3742C17.8663 10.8822 19.8899 10.0439 22 10.0439"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
          <path
            d="M14.0437 18C14.0437 15.8899 13.2055 13.8663 11.7134 12.3742C10.2214 10.8822 8.19772 10.0439 6.08765 10.0439"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
        </>
      )

    case 'ChevronUp':
      return (
        <>
          <path
            d="M13.9561 10C13.9561 12.1101 13.1178 14.1337 11.6258 15.6258C10.1337 17.1178 8.11008 17.9561 6 17.9561"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
          <path
            d="M13.9563 10C13.9563 12.1101 14.7945 14.1337 16.2866 15.6258C17.7786 17.1178 19.8023 17.9561 21.9124 17.9561"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
        </>
      )

    case 'Spinner':
      return (
        <path
          d="M50 7.617C26.594 7.617 7.617 26.594 7.617 50H0C0 22.387 22.387 0 50 0s50 22.387 50 50h-7.617C92.383 26.594 73.406 7.617 50 7.617zm0 0"
          fill={color}
          className="fill-current"
        />
      )

    case 'Cart':
      return (
        <>
          <path
            d="M24.0619 1H0.921875V24.912H24.0619V1Z"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="square"
          />
          <path
            d="M6.43091 5.229C6.43091 6.83648 7.06948 8.37812 8.20613 9.51478C9.34279 10.6514 10.8844 11.29 12.4919 11.29C14.0994 11.29 15.641 10.6514 16.7777 9.51478C17.9143 8.37812 18.5529 6.83648 18.5529 5.229"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="square"
          />
        </>
      )

    case 'Profile':
      return (
        <>
          <path
            d="M12.961 24.912C19.5669 24.912 24.922 19.5591 24.922 12.956C24.922 6.35288 19.5669 1 12.961 1C6.35512 1 1 6.35288 1 12.956C1 19.5591 6.35512 24.912 12.961 24.912Z"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="square"
          />
          <path
            d="M6.8999 14.229C6.8999 15.8365 7.53847 17.3781 8.67513 18.5148C9.81179 19.6514 11.3534 20.29 12.9609 20.29C14.5684 20.29 16.11 19.6514 17.2467 18.5148C18.3833 17.3781 19.0219 15.8365 19.0219 14.229"
            stroke={color}
            className="stroke-current"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="square"
          />
        </>
      )

    case 'Pause':
      return (
        <>
          <path
            d="M44 86C67.196 86 86 67.196 86 44C86 20.804 67.196 2 44 2C20.804 2 2 20.804 2 44C2 67.196 20.804 86 44 86Z"
            stroke={color}
            className="stroke-current"
            strokeWidth="4"
          />
          <path
            d="M34.3594 29.7344H37.875V59.2656H34.3594V29.7344ZM52.2891 29.7344H49.4766C49.2832 29.7344 49.125 29.8926 49.125 30.0859V58.9141C49.125 59.1074 49.2832 59.2656 49.4766 59.2656H52.2891C52.4824 59.2656 52.6406 59.1074 52.6406 58.9141V30.0859C52.6406 29.8926 52.4824 29.7344 52.2891 29.7344Z"
            fill={color}
            className="fill-current"
          />
        </>
      )

    case 'Play':
      return (
        <>
          <path
            d="M44 86C67.196 86 86 67.196 86 44C86 20.804 67.196 2 44 2C20.804 2 2 20.804 2 44C2 67.196 20.804 86 44 86Z"
            stroke={color}
            className="stroke-current"
            strokeWidth="4"
          />
          <path
            d="M56.4399 44C50.9979 44 45.7788 41.8381 41.9308 37.9901C38.0827 34.142 35.9209 28.9229 35.9209 23.481"
            stroke={color}
            className="stroke-current"
            strokeWidth="4"
            strokeMiterlimit="10"
          />
          <path
            d="M56.4399 44C50.9979 44 45.7788 46.1618 41.9308 50.0099C38.0827 53.8579 35.9209 59.077 35.9209 64.519"
            stroke={color}
            className="stroke-current"
            strokeWidth="4"
            strokeMiterlimit="10"
          />
        </>
      )

    case 'Quote':
      return (
        <path
          d="M8.4581 0L0 20H8.89861L14.0969 0H8.4581ZM22.8194 0L14.3612 20H23.2599L28.4581 0H22.8194Z"
          fill={color}
          className="fill-current"
        />
      )

    case 'PriceTag':
      return (
        <>
          <path
            className="fill-current"
            fill={color}
            d="M19.25 0h-6c-0.412 0-0.989 0.239-1.28 0.53l-7.439 7.439c-0.292 0.292-0.292 0.769 0 1.061l6.439 6.439c0.292 0.292 0.769 0.292 1.061 0l7.439-7.439c0.292-0.292 0.53-0.868 0.53-1.28v-6c0-0.412-0.337-0.75-0.75-0.75zM15.5 6c-0.828 0-1.5-0.672-1.5-1.5s0.672-1.5 1.5-1.5 1.5 0.672 1.5 1.5-0.672 1.5-1.5 1.5z"
          />
          <path
            className="fill-current"
            fill={color}
            d="M2 8.5l8.5-8.5h-1.25c-0.412 0-0.989 0.239-1.28 0.53l-7.439 7.439c-0.292 0.292-0.292 0.769 0 1.061l6.439 6.439c0.292 0.292 0.769 0.292 1.061 0l0.47-0.47-6.5-6.5z"
          />
        </>
      )

    case 'Copy':
      return (
        <>
          <path
            d="M5.33301 3.16668V15.6667C5.33301 16.2192 5.5525 16.7491 5.9432 17.1398C6.3339 17.5305 6.86381 17.75 7.41634 17.75H15.7497C16.3022 17.75 16.8321 17.5305 17.2228 17.1398C17.6135 16.7491 17.833 16.2192 17.833 15.6667V6.54376C17.833 6.26622 17.7775 5.99148 17.6698 5.73568C17.5621 5.47988 17.4044 5.24818 17.2059 5.05418L13.7528 1.67709C13.3636 1.29653 12.8409 1.08341 12.2965 1.08334H7.41634C6.86381 1.08334 6.3339 1.30284 5.9432 1.69354C5.5525 2.08424 5.33301 2.61414 5.33301 3.16668V3.16668Z"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stroke-current"
          />
          <path
            d="M13.6665 17.75V19.8334C13.6665 20.3859 13.447 20.9158 13.0563 21.3065C12.6656 21.6972 12.1357 21.9167 11.5832 21.9167H3.24984C2.6973 21.9167 2.1674 21.6972 1.7767 21.3065C1.386 20.9158 1.1665 20.3859 1.1665 19.8334V8.37502C1.1665 7.82249 1.386 7.29258 1.7767 6.90188C2.1674 6.51118 2.6973 6.29169 3.24984 6.29169H5.33317"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stroke-current"
          />
        </>
      )

    case 'Trash':
      return (
        <>
          <path d="M7.5 7.5H8.75V15H7.5V7.5Z" className="fill-current" />
          <path d="M11.25 7.5H12.5V15H11.25V7.5Z" className="fill-current" />
          <path
            d="M2.5 3.75V5H3.75V17.5C3.75 17.8315 3.8817 18.1495 4.11612 18.3839C4.35054 18.6183 4.66848 18.75 5 18.75H15C15.3315 18.75 15.6495 18.6183 15.8839 18.3839C16.1183 18.1495 16.25 17.8315 16.25 17.5V5H17.5V3.75H2.5ZM5 17.5V5H15V17.5H5Z"
            className="fill-current"
          />
          <path d="M7.5 1.25H12.5V2.5H7.5V1.25Z" className="fill-current" />
        </>
      )

    case 'Upload':
      return (
        <>
          <path
            d="M9.84375 6.78965H12.5861V19.3437C12.5861 19.507 12.7197 19.6406 12.883 19.6406H15.1096C15.2729 19.6406 15.4064 19.507 15.4064 19.3437V6.78965H18.1562C18.4049 6.78965 18.5422 6.50391 18.39 6.31094L14.2338 1.04884C14.206 1.01336 14.1705 0.984663 14.13 0.964928C14.0895 0.945193 14.0451 0.934937 14 0.934937C13.9549 0.934937 13.9105 0.945193 13.87 0.964928C13.8295 0.984663 13.794 1.01336 13.7662 1.04884L9.60996 6.30723C9.45781 6.50391 9.59512 6.78965 9.84375 6.78965ZM27.582 18.2304H25.3555C25.1922 18.2304 25.0586 18.364 25.0586 18.5273V24.2422H2.94141V18.5273C2.94141 18.364 2.80781 18.2304 2.64453 18.2304H0.417969C0.254688 18.2304 0.121094 18.364 0.121094 18.5273V25.875C0.121094 26.5318 0.651758 27.0625 1.30859 27.0625H26.6914C27.3482 27.0625 27.8789 26.5318 27.8789 25.875V18.5273C27.8789 18.364 27.7453 18.2304 27.582 18.2304Z"
            className="fill-current"
          />
        </>
      )

    case 'Edit':
      return (
        <>
          <path
            d="M20.3125 12.5C20.3125 12.2928 20.3948 12.0941 20.5413 11.9476C20.6878 11.8011 20.8865 11.7188 21.0938 11.7188C21.301 11.7188 21.4997 11.8011 21.6462 11.9476C21.7927 12.0941 21.875 12.2928 21.875 12.5V21.0938C21.875 21.301 21.7927 21.4997 21.6462 21.6462C21.4997 21.7927 21.301 21.875 21.0938 21.875H3.90625C3.69905 21.875 3.50034 21.7927 3.35382 21.6462C3.20731 21.4997 3.125 21.301 3.125 21.0938V3.90625C3.125 3.69905 3.20731 3.50034 3.35382 3.35382C3.50034 3.20731 3.69905 3.125 3.90625 3.125H12.5C12.7072 3.125 12.9059 3.20731 13.0524 3.35382C13.1989 3.50034 13.2812 3.69905 13.2812 3.90625C13.2812 4.11345 13.1989 4.31216 13.0524 4.45868C12.9059 4.60519 12.7072 4.6875 12.5 4.6875H4.6875V20.3125H20.3125V12.5Z"
            className="fill-current"
          />
          <path
            d="M11.4737 13.5313L12.7627 13.3469L20.6814 5.42971C20.7561 5.35764 20.8156 5.27144 20.8565 5.17612C20.8975 5.0808 20.919 4.97829 20.9199 4.87456C20.9208 4.77082 20.9011 4.66795 20.8618 4.57193C20.8225 4.47592 20.7645 4.38869 20.6911 4.31534C20.6178 4.24199 20.5305 4.18398 20.4345 4.14469C20.3385 4.10541 20.2356 4.08565 20.1319 4.08655C20.0282 4.08745 19.9257 4.109 19.8303 4.14994C19.735 4.19089 19.6488 4.25041 19.5768 4.32502L11.6565 12.2422L11.4721 13.5313H11.4737ZM21.7861 3.21877C22.0039 3.43643 22.1767 3.69487 22.2946 3.97933C22.4125 4.26378 22.4731 4.56867 22.4731 4.87659C22.4731 5.1845 22.4125 5.48939 22.2946 5.77385C22.1767 6.0583 22.0039 6.31674 21.7861 6.5344L13.6846 14.636C13.5651 14.7559 13.4099 14.8337 13.2424 14.8578L10.6643 15.2266C10.5441 15.2438 10.4216 15.2329 10.3064 15.1945C10.1913 15.1562 10.0866 15.0915 10.0008 15.0057C9.91496 14.9199 9.85031 14.8152 9.81197 14.7C9.77363 14.5849 9.76265 14.4624 9.77991 14.3422L10.1487 11.7641C10.1723 11.5967 10.2496 11.4416 10.369 11.3219L18.4721 3.22034C18.9116 2.78095 19.5076 2.53412 20.1291 2.53412C20.7506 2.53412 21.3466 2.78095 21.7861 3.22034V3.21877Z"
            className="fill-current"
          />
        </>
      )

    case 'Store':
      return (
        <path
          d="M26.6659 16.5333V17.6V26.1333H12.7992V17.6V16.5333M26.6659 16.5333H28.7992V10.6667L26.6659 4.79999H5.33255L3.19922 10.6667V16.5333H4.79922M26.6659 16.5333H22.9326M4.79922 16.5333V26.6667M4.79922 16.5333H9.06589M12.7992 16.5333H15.9992M12.7992 16.5333H9.06589M22.9326 16.5333V12.2667M22.9326 16.5333H15.9992M15.9992 16.5333V12.2667M15.9992 16.5333H9.06589M9.06589 16.5333V12.2667"
          strokeWidth="1.5"
          className="stroke-current"
        />
      )

    case 'Logout':
      return (
        <>
          <path
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stroke-current"
            d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2"
          ></path>
          <path
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stroke-current"
            d="M7 12h14l-3 -3m0 6l3 -3"
          ></path>
        </>
      )

    case 'Info': {
      return (
        <path
          d="M9 6H9.0075M9 8.25V12M15.75 9C15.75 12.728 12.728 15.75 9 15.75C5.27208 15.75 2.25 12.728 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25C12.728 2.25 15.75 5.27208 15.75 9Z"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="stroke-current"
        />
      )
    }

    default:
      return <path />
  }
}

const getDefaultViewBox = (iconName: IconName) => {
  switch (iconName) {
    case 'Apple':
      return '0 0 50 60'

    case 'Facebook':
      return '0 0 31 60'

    case 'Instagram':
      return '0 0 61 60'

    case 'Pinterest':
      return '0 0 63 64'

    case 'Soundcloud':
      return '0 0 66 29'

    case 'Spotify':
      return '0 0 56 37'

    case 'Twitter':
      return '0 0 50 41'

    case 'YouTube':
      return '0 0 38 30'

    case 'Github':
      return '0 0 62 60'

    case 'TikTok': {
      return '0 0 32 32'
    }

    case 'ArrowLeft':
      return '0 0 33 22'

    case 'ArrowRight':
      return '0 0 33 22'

    case 'Plus':
      return '0 0 28 28'

    case 'Minus':
      return '0 0 28 28'

    case 'PlusCircle':
      return '0 0 45 45'

    case 'Checkmark':
      return '0 0 28 28'

    case 'Cross':
      return '0 0 28 28'

    case 'ChevronLeft':
      return '0 0 28 28'

    case 'ChevronRight':
      return '0 0 28 28'

    case 'ChevronDown':
      return '0 0 28 28'

    case 'ChevronUp':
      return '0 0 28 28'

    case 'Cart':
      return '0 0 25 26'

    case 'Profile':
      return '0 0 26 26'

    case 'Play':
      return '0 0 88 88'

    case 'Pause':
      return '0 0 88 88'

    case 'Quote':
      return '0 0 29 20'

    case 'PriceTag':
      return '0 0 20 16'

    case 'Copy':
      return '0 0 19 23'

    case 'Trash':
      return '0 0 20 20'

    case 'Upload':
      return '0 0 28 28'

    case 'Edit':
      return '0 0 25 25'

    case 'Store':
      return '0 0 32 32'

    case 'Logout':
      return '0 0 24 24'

    case 'Info': {
      return '0 0 18 18'
    }

    default:
      return '0 0 100 100'
  }
}

export default Icon
